import type {
  GlobalSearchCategory,
  SearchResultNavigationContext,
} from '../../../../../switch-shared/dist/modules/features/global-search/global-search.dto';
import {
  buildApplicationGroupPath,
  buildApplicationPath,
  buildApplicationScopedPath,
  buildGroupScopedPath,
  buildUserPath,
} from '../../../router/pathUtils';
import pages from '../../../router/routes';
import { ALL_SELECTION_ID } from '../../../utils/selection';

export interface SearchNavigationResult {
  id: string;
  title?: string;
  type: GlobalSearchCategory | string;
  navigationContext?: SearchResultNavigationContext;
}

export interface SearchResultNavigationTarget {
  path: string;
  groupId?: string;
  applicationId?: string;
  state?: {
    breadcrumbName?: string;
  };
}

const getSearchResultBreadcrumbState = (result: SearchNavigationResult) =>
  result.title ? { breadcrumbName: result.title } : undefined;

const getGroupScopedSearchPath = ({
  isUrlNavigationEnabled,
  applicationGroupId,
  sectionPath,
  childPath,
  legacyPath,
}: {
  isUrlNavigationEnabled: boolean;
  applicationGroupId?: string;
  sectionPath: string;
  childPath?: string;
  legacyPath: string;
}) =>
  isUrlNavigationEnabled && applicationGroupId
    ? buildGroupScopedPath(applicationGroupId, sectionPath, childPath)
    : legacyPath;

const getApplicationScopedSearchPath = ({
  isUrlNavigationEnabled,
  applicationGroupId,
  applicationId,
  sectionPath,
  childPath,
  legacyPath,
}: {
  isUrlNavigationEnabled: boolean;
  applicationGroupId?: string;
  applicationId?: string;
  sectionPath: string;
  childPath?: string;
  legacyPath: string;
}) =>
  isUrlNavigationEnabled && applicationGroupId && applicationId
    ? buildApplicationScopedPath(
        applicationGroupId,
        applicationId,
        sectionPath,
        childPath
      )
    : legacyPath;

export const getUserSearchGroupId = (
  navigationContext?: SearchResultNavigationContext
) =>
  navigationContext?.applicationGroupId ||
  (navigationContext?.allApplicationGroups ? ALL_SELECTION_ID : undefined);

const getTariffSearchGroupId = (
  navigationContext?: SearchResultNavigationContext
) =>
  navigationContext?.applicationGroupId ||
  (navigationContext?.allApplicationGroups ? ALL_SELECTION_ID : undefined) ||
  ALL_SELECTION_ID;

export const buildSearchResultNavigationTarget = ({
  result,
  navigationContext = result.navigationContext,
  isUrlNavigationEnabled,
}: {
  result: SearchNavigationResult;
  navigationContext?: SearchResultNavigationContext;
  isUrlNavigationEnabled: boolean;
}): SearchResultNavigationTarget => {
  const applicationGroupId = navigationContext?.applicationGroupId;
  const applicationId = navigationContext?.applicationId;
  const userApplicationGroupId = getUserSearchGroupId(navigationContext);
  const tariffApplicationGroupId = getTariffSearchGroupId(navigationContext);
  const state = getSearchResultBreadcrumbState(result);

  switch (result.type) {
    case 'applicationGroups':
      return {
        path: isUrlNavigationEnabled
          ? buildApplicationGroupPath(result.id)
          : pages.applicationGroup.view.path.replace(':id', result.id),
        groupId: result.id,
        state,
      };

    case 'users':
      return {
        path:
          isUrlNavigationEnabled && userApplicationGroupId
            ? buildUserPath(userApplicationGroupId, result.id)
            : pages.users.view.path.replace(':id', result.id),
        groupId: userApplicationGroupId,
        state,
      };

    case 'applications':
      return {
        path:
          isUrlNavigationEnabled && applicationGroupId
            ? buildApplicationPath(applicationGroupId, result.id)
            : pages.application.view.path.replace(':id', result.id),
        groupId: applicationGroupId,
        applicationId: result.id,
        state,
      };

    case 'tariffs':
      return {
        path: getGroupScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId: tariffApplicationGroupId,
          sectionPath: 'tariffs',
          childPath: result.id,
          legacyPath: pages.tariffs.view.path.replace(':id', result.id),
        }),
        groupId: tariffApplicationGroupId,
        state,
      };

    case 'accounts':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: 'accounts',
          childPath: result.id,
          legacyPath: pages.accounts.view.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    case 'utilities':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: 'utilities',
          childPath: result.id,
          legacyPath: pages.utility.view.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    case 'meterPoints':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: pages.meterpoints.name,
          childPath: result.id,
          legacyPath: pages.meterpoints.view.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    case 'zones':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: 'zones',
          childPath: result.id,
          legacyPath: pages.zones.view.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    case 'meters':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: 'meters',
          childPath: result.id,
          legacyPath: pages.meters.view.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    case 'gateways':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: 'gateways',
          childPath: result.id,
          legacyPath: pages.gateways.viewGateway.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    case 'sensors':
      return {
        path: getApplicationScopedSearchPath({
          isUrlNavigationEnabled,
          applicationGroupId,
          applicationId,
          sectionPath: 'sensors',
          childPath: result.id,
          legacyPath: pages.sensors.view.path.replace(':id', result.id),
        }),
        groupId: applicationGroupId,
        applicationId,
        state,
      };

    default:
      return { path: '#' };
  }
};
